import type { ContactFieldDto, SegmentDto } from '@carbonwebmail/contracts'
import type { SegmentSort } from '@carbonwebmail/domain'
import { SEGMENT_COLUMNS } from '@carbonwebmail/domain'
import { count, when } from '../../ui/format.js'
import { describeSegment } from './describeSegment.js'

/**
 * What the segments board can show, as data.
 *
 * The same shape the contacts board uses and for the same reason: three things
 * ask what the columns are — what to draw, what can be ordered by, what can be
 * hidden — and three copies is three places to forget one.
 *
 * **The four built-in ones come from the domain** (`SEGMENT_COLUMNS`), because
 * a saved view stores which columns it hides and the sort a person chooses
 * names one of their ids. The declared ones are added here.
 */

/**
 * A column on the board: one a segment always has, or one declared for it.
 *
 * The same two kinds the contacts board has, and the difference is the same —
 * where the label is stored. A declared one carries its own on its own row and
 * can be renamed, retyped and deleted; the four built in are renamed through
 * `segment_column_labels` and cannot be retyped or deleted, because the data
 * would still be there.
 */
export interface BoardSegmentColumn {
  readonly id: string
  readonly label: string
  readonly kind: 'base' | 'field'
  /** The declared field's key, for a `field`. What a rule and a token name. */
  readonly key: string | undefined
  readonly sortable: boolean
  readonly fixed: boolean
}

/**
 * What the board can show: the four it always has, then one per declared field.
 *
 * A declared column offers no sort. Its values are text in jsonb, and a control
 * that ordered by one would be answering a different question from the one the
 * heading implies — the same reason "Asks for" offers none.
 *
 * `labels` holds only the overrides — a built-in column with no entry is called
 * whatever the domain says, which is how a default that changes in a later
 * release reaches everybody who never renamed it.
 */
export function segmentBoardColumns(
  fields: readonly ContactFieldDto[],
  labels: Readonly<Record<string, string>> = {},
): readonly BoardSegmentColumn[] {
  return [
    ...SEGMENT_COLUMNS.map((column) => ({
      id: column.id,
      label: labels[column.id] ?? column.defaultLabel,
      kind: 'base' as const,
      key: undefined,
      sortable: column.sortable,
      fixed: column.fixed,
    })),
    ...fields.map((field) => ({
      id: `field:${field.key}`,
      label: field.label,
      kind: 'field' as const,
      key: field.key,
      sortable: false,
      fixed: false,
    })),
  ]
}

/** What a row shows in each column. Prose for the rules, dates as "3 days ago". */
export function valueIn(segment: SegmentDto, column: BoardSegmentColumn): string {
  if (column.key !== undefined) return segment.custom[column.key] ?? ''
  if (column.id === 'name') return segment.name
  if (column.id === 'asks') return describeSegment(segment.definition)
  // Connected, not reached: a segment's audience is its rules or its members,
  // and this is the membership. A rules-only segment reads zero, which is true.
  if (column.id === 'connected') return count(segment.connected)
  return when(column.id === 'created' ? segment.createdAt : segment.updatedAt)
}

/**
 * Orders two segments by a column.
 *
 * In the browser, unlike the contacts board. The whole list already arrives in
 * one response — a tenant has tens of segments — so asking the server to order
 * it would be a second answer to a question already answered.
 *
 * Dates compare as their ISO strings, which sort the same as the instants they
 * name. Names compare with `localeCompare`, so "Ärsredovisning" lands where a
 * person looks for it rather than after "Z".
 */
export function ordered(
  segments: readonly SegmentDto[],
  sort: SegmentSort | undefined,
): readonly SegmentDto[] {
  // The board's own order: most recently changed first, which is what somebody
  // coming back to a list they were working on is looking for.
  if (sort === undefined) {
    return [...segments].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  }

  const way = sort.direction === 'asc' ? 1 : -1
  return [...segments].sort((a, b) => way * compare(a, b, sort.column))
}

function compare(a: SegmentDto, b: SegmentDto, column: SegmentSort['column']): number {
  if (column === 'created') return a.createdAt.localeCompare(b.createdAt)
  if (column === 'changed') return a.updatedAt.localeCompare(b.updatedAt)
  return a.name.localeCompare(b.name)
}

/** Whether the search box matches a segment. Its name and what it asks for. */
export function matches(segment: SegmentDto, term: string): boolean {
  const looking = term.trim().toLowerCase()
  if (looking === '') return true
  return (
    segment.name.toLowerCase().includes(looking) ||
    describeSegment(segment.definition).toLowerCase().includes(looking)
  )
}
